// Priority colors and labels

export const PRIORITY_COLORS: Record<Priority, string> = {
  1: 'var(--priority-1)',
  2: 'var(--priority-2)',
  3: 'var(--priority-3)',
  4: 'var(--priority-4)',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  1: 'P1',
  2: 'P2',
  3: 'P3',
  4: 'P4',
};

// Core types

export type Priority = 1 | 2 | 3 | 4;
export type TaskSection = 'overdue' | 'today' | 'upcoming' | 'later' | 'completed';

export interface SubTask {
  id: string;
  title: string;
  completed: boolean;
}

export interface Todo {
  id: string;
  title: string;
  description: string;
  priority: Priority;
  dueDate: Date | null;
  completed: boolean;
  project: string | null;
  assignee: string | null;
  subTasks: SubTask[];
  blockedBy: string[];
  section: TaskSection;
  createdAt: Date;
}

// Projects

export interface Project {
  id: string;
  name: string;
  color: string; // design-token color value
}

export const MOCK_PROJECTS: Project[] = [
  { id: 'work', name: 'Work', color: 'var(--accent)' },
  { id: 'personal', name: 'Personal', color: 'var(--success)' },
  { id: 'urgent', name: 'Urgent', color: 'var(--error)' },
  { id: 'longterm', name: 'Long-term', color: 'var(--warning)' },
  { id: 'shopping', name: 'Shopping', color: 'var(--signal-memory)' },
];

// NLP parsed result

export interface ParsedTaskInput {
  title: string;
  date: Date | null;
  priority: Priority;
  project: string | null;
  assignee: string | null;
}

// Helpers

export function getProjectColor(projectId: string | null): string {
  if (!projectId) return 'var(--text-tertiary)';
  return MOCK_PROJECTS.find((p) => p.id === projectId)?.color ?? 'var(--text-tertiary)';
}

export function getProjectName(projectId: string | null): string {
  if (!projectId) return '';
  return MOCK_PROJECTS.find((p) => p.id === projectId)?.name ?? projectId;
}

export function formatDueDate(date: Date | null): string {
  if (!date) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffMs = target.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    if (diffDays === -1) return 'Yesterday';
    return `${Math.abs(diffDays)}d overdue`;
  }
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (diffDays < 7) return weekdays[target.getDay()]!;
  return `${months[target.getMonth()]} ${target.getDate()}`;
}

export function isOverdue(date: Date | null): boolean {
  if (!date) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return target < today;
}

export function isToday(date: Date | null): boolean {
  if (!date) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export function sectionFromDate(date: Date | null, completed: boolean): TaskSection {
  if (completed) return 'completed';
  if (!date) return 'upcoming';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffMs = target.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'overdue';
  if (diffDays === 0) return 'today';
  if (diffDays <= 7) return 'upcoming';
  return 'later';
}

// Mock data

// Today: April 14, 2026
const BASE = new Date(2026, 3, 14); // April 14, 2026 (month is 0-indexed)

function d(daysFromBase: number): Date {
  const r = new Date(BASE);
  r.setDate(r.getDate() + daysFromBase);
  return r;
}

function noDate(): Date | null {
  return null;
}

export const MOCK_TODOS: Todo[] = [
  // Overdue
  {
    id: 't1',
    title: 'Review Q3 campaign proposal',
    description:
      'Sarah sent the deck on March 28. Marketing wants feedback before the quarterly planning meeting. Check the budget projections on slide 12.',
    priority: 1,
    dueDate: d(-7), // Apr 7
    completed: false,
    project: 'work',
    assignee: null,
    subTasks: [
      { id: 'st1', title: 'Read full proposal', completed: true },
      { id: 'st2', title: 'Check budget numbers', completed: false },
      { id: 'st3', title: 'Add comments to deck', completed: false },
    ],
    blockedBy: [],
    section: 'overdue',
    createdAt: d(-10),
  },
  {
    id: 't2',
    title: 'Send invoice to Acme Corp',
    description:
      'Net-30 terms. Invoice #2026-0389. Services rendered: consulting, March 1-31.',
    priority: 2,
    dueDate: d(-3), // Apr 11
    completed: false,
    project: 'work',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'overdue',
    createdAt: d(-14),
  },

  // Today
  {
    id: 't3',
    title: 'Prep weekly all-hands deck',
    description: '',
    priority: 1,
    dueDate: d(0), // Apr 14, today
    completed: false,
    project: 'work',
    assignee: null,
    subTasks: [
      { id: 'st4', title: 'Update metrics slide', completed: false },
      { id: 'st5', title: 'Add team highlights', completed: false },
    ],
    blockedBy: [],
    section: 'today',
    createdAt: d(-1),
  },
  {
    id: 't4',
    title: 'Call Dr. Jensen re: test results',
    description: '',
    priority: 2,
    dueDate: d(0),
    completed: false,
    project: 'personal',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'today',
    createdAt: d(-2),
  },
  {
    id: 't5',
    title: 'Renew car insurance - compare 3 quotes',
    description: 'Policy expires May 1. Geico, Lemonade, and State Farm quotes need comparison.',
    priority: 2,
    dueDate: d(0),
    completed: false,
    project: 'urgent',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'today',
    createdAt: d(-5),
  },
  {
    id: 't6',
    title: 'Schedule 1:1 with Alex',
    description: '',
    priority: 3,
    dueDate: d(0),
    completed: true,
    project: 'work',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'completed',
    createdAt: d(-3),
  },
  {
    id: 't7',
    title: 'Buy birthday gift for Mom',
    description: '',
    priority: 2,
    dueDate: d(0),
    completed: true,
    project: 'personal',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'completed',
    createdAt: d(-7),
  },

  // Upcoming (next 7 days)
  {
    id: 't8',
    title: 'Submit quarterly tax estimate',
    description: '',
    priority: 2,
    dueDate: d(3), // Apr 17
    completed: false,
    project: 'urgent',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'upcoming',
    createdAt: d(-7),
  },
  {
    id: 't9',
    title: 'Book flight for June conference',
    description: 'AWS re:Invent early-bird deadline is Apr 20.',
    priority: 3,
    dueDate: d(4), // Apr 18
    completed: false,
    project: 'work',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'upcoming',
    createdAt: d(-1),
  },
  {
    id: 't10',
    title: 'Set up new laptop - dev environment',
    description: '',
    priority: 3,
    dueDate: d(5), // Apr 19
    completed: false,
    project: 'personal',
    assignee: null,
    subTasks: [
      { id: 'st6', title: 'Install Homebrew', completed: false },
      { id: 'st7', title: 'Clone signa repo', completed: false },
      { id: 'st8', title: 'Run pnpm install', completed: false },
    ],
    blockedBy: [],
    section: 'upcoming',
    createdAt: d(0),
  },
  {
    id: 't11',
    title: 'Update LinkedIn profile',
    description: '',
    priority: 4,
    dueDate: d(6), // Apr 20
    completed: false,
    project: 'personal',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'upcoming',
    createdAt: d(-3),
  },

  // Later (>7 days)
  {
    id: 't12',
    title: 'Plan summer vacation',
    description: 'Look at Croatia itinerary options. Need flights + Airbnb.',
    priority: 3,
    dueDate: d(14), // Apr 28
    completed: false,
    project: 'longterm',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'later',
    createdAt: d(-14),
  },
  {
    id: 't13',
    title: 'Annual performance review prep',
    description: '',
    priority: 3,
    dueDate: d(21), // May 5
    completed: false,
    project: 'work',
    assignee: null,
    subTasks: [
      { id: 'st9', title: 'Gather achievements', completed: false },
      { id: 'st10', title: 'Write self-assessment', completed: false },
      { id: 'st11', title: 'Collect peer feedback', completed: false },
    ],
    blockedBy: [],
    section: 'later',
    createdAt: d(-7),
  },
  {
    id: 't14',
    title: 'Pick up prescription at pharmacy',
    description: '',
    priority: 1,
    dueDate: noDate(),
    completed: false,
    project: 'personal',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'upcoming',
    createdAt: d(-1),
  },
  {
    id: 't15',
    title: 'Finish reading "Slow Productivity"',
    description: '',
    priority: 4,
    dueDate: noDate(),
    completed: false,
    project: 'longterm',
    assignee: null,
    subTasks: [],
    blockedBy: [],
    section: 'upcoming',
    createdAt: d(-21),
  },
];

// Derived: compute section from date for non-completed tasks
export const MOCK_TODOS_WITH_SECTIONS: Todo[] = MOCK_TODOS.map((t) => ({
  ...t,
  section: sectionFromDate(t.dueDate, t.completed),
}));
